import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContext } from "../../../../../hooks/context";
import {
  useApproveStockRemovalRequest,
  useRejectStockRemovalRequest,
  useSubmitStockRemovalRequestDraft,
  useDeleteStockRemovalRequest,
} from "../../../../../hooks/api/mutations/useInventory";
import { useGetStockRemovalRequestDetails } from "../../../../../hooks/api/queries/useInventory";
import { pathConstants } from "../../../../../routes/pathContants";

const useStockRemovalReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const triggerToast = useContext(ToastContext);

  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditDraftModalOpen, setIsEditDraftModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionError, setRejectionError] = useState("");

  const {
    data: removalDetails,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
  } = useGetStockRemovalRequestDetails({ id });

  const { mutate: approveMutation, isLoading: isApproving } =
    useApproveStockRemovalRequest();
  const { mutate: rejectMutation, isLoading: isRejecting } =
    useRejectStockRemovalRequest();
  const { mutate: submitDraftMutation, isLoading: isSubmittingDraft } =
    useSubmitStockRemovalRequestDraft();
  const { mutate: deleteMutation, isLoading: isDeleting } =
    useDeleteStockRemovalRequest();

  const handleApprove = () => {
    approveMutation(id, {
      onSuccess: (res) => {
        triggerToast(
          res?.data?.detail || "Stock removal request approved successfully.",
          "success",
        );
        setIsApproveModalOpen(false);
        refetchDetails();
      },
    });
  };

  const handleReject = (reason) => {
    const finalReason = typeof reason === "string" ? reason : rejectionReason;
    if (!finalReason.trim()) {
      setRejectionError("Please provide a reason for rejection.");
      return;
    }
    setRejectionError("");

    rejectMutation(
      { id, reason: finalReason.trim() },
      {
        onSuccess: (res) => {
          triggerToast(
            res?.data?.detail || "Stock removal request rejected successfully.",
            "success",
          );
          setIsRejectModalOpen(false);
          setRejectionReason("");
          refetchDetails();
        },
      },
    );
  };

  const handleSubmitDraft = () => {
    submitDraftMutation(id, {
      onSuccess: (res) => {
        triggerToast(
          res?.data?.detail || "Stock removal request submitted for approval.",
          "success",
        );
        refetchDetails();
      },
    });
  };

  const handleDelete = () => {
    deleteMutation(id, {
      onSuccess: (res) => {
        triggerToast(
          res?.data?.detail || "Stock removal request deleted successfully.",
          "success",
        );
        navigate(pathConstants.INVENTORY_STOCK_REMOVALS);
      },
    });
  };

  const handleBack = () => {
    navigate(pathConstants.INVENTORY_STOCK_REMOVALS);
  };

  return {
    id,
    removalDetails,
    isLoadingDetails,
    isApproving,
    isRejecting,
    isSubmittingDraft,
    isDeleting,
    isApproveModalOpen,
    setIsApproveModalOpen,
    isRejectModalOpen,
    setIsRejectModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isEditDraftModalOpen,
    setIsEditDraftModalOpen,
    previewImage,
    setPreviewImage,
    rejectionReason,
    setRejectionReason,
    rejectionError,
    setRejectionError,
    handleApprove,
    handleReject,
    handleSubmitDraft,
    handleDelete,
    handleBack,
    refetchDetails,
  };
};

export default useStockRemovalReview;
